import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';
import { getFullLoad, getSmallLoadSurcharge, getSmallLoadThreshold } from '../utils/loadSplit';
/**
 * Shared small-load warning used across the Hero estimator, the materials
 * hub product cards, the Product detail page — anywhere we surface
 * single-load pricing.
 *
 * Two distinct cases:
 *
 * 1. MICRO-LOAD (quantity < 3 yd): manually-entered 1–2 yd loads are
 *    accepted — never rejected — and priced with a flat $250 delivery fee
 *    (no per-yard surcharge). We explain the flat fee and nudge the
 *    customer to round up to 3+ yd to drop it.
 *
 * 2. SMALL-LOAD SURCHARGE (3 ≤ quantity < threshold): a single load
 *    below the truck's threshold (12 yd light / 10 yd heavy) is billed at
 *    a higher per-yard rate. The info panel explains the delivery-fee vs
 *    per-yard split so customers see this isn't an extra charge — it's
 *    just the math of running a truck below capacity. Requires
 *    pricePerYard to render — without it, falls through to return null.
 *
 * Multi-delivery orders (quantity > full load) get partial-load pricing
 * handled per-line in the cart breakdown, so this component returns null
 * in those cases.
 */
export function SmallLoadBreakdown({
  quantity,
  productName,
  pricePerYard,
  className = ''
}: {
  quantity: number;
  productName: string;
  pricePerYard?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!quantity || quantity <= 0) return null;
  const fullLoad = getFullLoad(productName);
  if (quantity > fullLoad) return null;
  const threshold = getSmallLoadThreshold(productName);
  // Case 1 — Micro-load (< 3 yd) flat delivery fee
  if (quantity < 3) {
    return <div className={className} data-id="element-2011" data-mp="3oh3gl9ltsfqn">
        <button type="button" onClick={() => setOpen(v => !v)} className="inline-flex items-start gap-1.5 text-left text-xs text-[#CB6E0A] dark:text-[#F6A93B] font-medium hover:underline" aria-expanded={open} data-id="element-2012" data-mp="3og1wygqqukxb">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-2013" data-mp="1tcnb8akjoz5v" />
          <span data-id="element-2014" data-mp="1of3xrcvo8qez">Includes $250 micro-load delivery fee. Why?</span>
        </button>
        <AnimatePresence data-id="element-2015" data-mp="1oh84e2kyg3iy">
          {open && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-2016" data-mp="22ewnp5zdj2lw">
              <div className="mt-2 p-3 rounded-lg bg-[#CB6E0A]/10 dark:bg-[#F6A93B]/10 border border-[#CB6E0A]/30 dark:border-[#F6A93B]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-2017" data-mp="15p5iabpjnv1b">
                <p data-id="element-2018" data-mp="1enzjkfdtl9l7">
                  <span className="font-bold" data-id="element-2019" data-mp="1enhdkr1cvh03">
                    Micro-load delivery minimum.
                  </span>{' '}
                  Trucks have fixed dispatch costs (driver time, fuel, routing).
                  Delivering 1 or 2 yards requires a flat $250 delivery fee to
                  cover the truck run.
                </p>
                <p className="text-stone-900 dark:text-white font-medium" data-id="element-2020" data-mp="hkebkfar8zcc">
                  Tip: Round up to 3+ yards to drop the flat fee and switch to
                  standard zone delivery rates.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }
  // Case 2 — small-load surcharge (only when we know the base price)
  if (pricePerYard && pricePerYard > 0 && quantity >= 3 && quantity < threshold) {
    const surcharge = getSmallLoadSurcharge(quantity, productName);
    const effectivePerYard = Math.ceil(pricePerYard * surcharge);
    return <div className={className} data-id="element-2021" data-mp="ql2o15ki2rn4">
        <button type="button" onClick={() => setOpen(v => !v)} className="inline-flex items-start gap-1.5 text-left text-xs text-[#CB6E0A] dark:text-[#F6A93B] font-medium hover:underline" aria-expanded={open} data-id="element-2022" data-mp="qkb1ek8vs48z">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-2023" data-mp="3bd4u6mcuq05j" />
          <span data-id="element-2024" data-mp="3rig7afkrp2az">
            Includes small load upcharge (${effectivePerYard}/yd vs $
            {pricePerYard}/yd at {threshold}+ yards). Why?
          </span>
        </button>
        <AnimatePresence data-id="element-2025" data-mp="3mq56zbm47ehw">
          {open && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-2026" data-mp="3hs3oarslph6y">
              <div className="mt-2 p-3 rounded-lg bg-[#CB6E0A]/10 dark:bg-[#F6A93B]/10 border border-[#CB6E0A]/30 dark:border-[#F6A93B]/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-2" data-id="element-2027" data-mp="9ctpptnx6u3x">
                <p data-id="element-2028" data-mp="3vj9tn4ika91r">
                  <span className="font-bold" data-id="element-2029" data-mp="3vjruyfk2zdgj">
                    Why both a delivery fee and a small-load rate?
                  </span>{' '}
                  They cover two different things.
                </p>
                <ul className="space-y-1.5 list-none pl-0" data-id="element-2030" data-mp="2k78yn7v3todk">
                  <li data-id="element-2031" data-mp="zo54u37j4oog">
                    <span className="font-semibold text-stone-800 dark:text-white" data-id="element-2032" data-mp="zoomrjx6jd2h">
                      Delivery fee
                    </span>{' '}
                    = the round-trip to your address (distance, fuel, driver
                    time). Same whether the truck is full or half-empty.
                  </li>
                  <li data-id="element-2033" data-mp="1edt9neex65gx">
                    <span className="font-semibold text-stone-800 dark:text-white" data-id="element-2034" data-mp="1jbcuwya1biid">
                      Per-yard rate
                    </span>{' '}
                    = the material plus loading it at the yard (loader operator,
                    scale, ticketing). That loading work takes about the same
                    time whether we scoop 3 yds or {fullLoad} yds.
                  </li>
                </ul>
                <p data-id="element-2035" data-mp="3u3xjaz9v0mog">
                  When you order less than a full load, that fixed loading cost
                  is split across fewer yards — so the per-yard price goes up.
                  It's not an extra fee, just the math of running a truck below
                  capacity.
                </p>
                <p className="text-stone-600 dark:text-white/70" data-id="element-2036" data-mp="3n0zhace17tw1">
                  Tip: rounding up to {threshold}+ yards drops you to{' '}
                  <span className="font-bold text-stone-800 dark:text-white" data-id="element-2037" data-mp="4rym1m32lq28">
                    ${pricePerYard}/yd
                  </span>{' '}
                  — usually more material for not much more money.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }
  return null;
}